import * as THREE from "three"
import * as TWEEN from "@tweenjs/tween.js"
import {
  Euler,
  Material,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  PerspectiveCamera,
  RGBM7Encoding,
  Vector2,
  Vector3,
} from "three"
import { TGALoader } from "three/examples/jsm/loaders/TGALoader"
import HdrFilePath_Roof from "./lightmaps/gtc-x_krov-ploca_lightmap_group_final_RGBM.tga"
import HdrFilePath_RoofRail from "./lightmaps/gtc-x_krov-ograda_lightmap_group_final_RGBM.tga"
import Hdr2FilePath_VII from "./lightmaps/gtc-x_VII-sprat_lightmap_group_final_RGBM.tga"
import Hdr2FilePath_VIII from "./lightmaps/gtc-x_VIII-sprat_lightmap_group_final_RGBM.tga"
import { OrbitControls } from "./OrbitControl/OrbitControl"
export class AnimationController {
  scene: THREE.Scene
  chairs: THREE.Object3D[] | undefined

  krov: THREE.Object3D[] | undefined
  lights: THREE.Object3D[] | undefined
  cameraPositions: THREE.Object3D[] | undefined
  target_1: THREE.Object3D | undefined
  target_2: THREE.Object3D | undefined
  layout_1: THREE.Object3D | undefined
  logo: THREE.Object3D | undefined
  layout_2: THREE.Object3D | undefined
  floor_VII: THREE.Object3D | undefined
  floor_VIII: THREE.Object3D | undefined
  camera: THREE.PerspectiveCamera
  box: THREE.Object3D[] | undefined
  controller: OrbitControls
  cameraTarget: Vector3
  zoomedIn = false
  tweenedOut = false
  texture_VII: THREE.Texture | undefined = undefined
  texture_VIII: THREE.Texture | undefined = undefined
  texture_roof: THREE.Texture | undefined = undefined
  texture_fence: THREE.Texture | undefined = undefined
  finishedTransition = true

  constructor(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    controller: OrbitControls,
  ) {
    this.scene = scene
    this.camera = camera
    this.controller = controller
    this.cameraTarget = new Vector3()

    // this.camera = this.fixedCamera
  }

  animate() {
    TWEEN.update()
  }

  switchFixedCamera(index: number) {
    if (this.cameraPositions) {
      if (this.controller.enabled) {
        this.camera.getWorldPosition(this.startPosition)
        this.camera.rotation.copy(this.startRotation)
        this.controller.enabled = false
      }

      const pos = this.cameraPositions[index].position
      const rot = this.cameraPositions[index].userData.objectData.trans.rotation

      this.camera.position.set(pos.x, pos.y, pos.z)
      const q = new THREE.Quaternion(rot.x, rot.y, rot.z, rot.w)

      const v = new Euler()
      v.setFromQuaternion(q)
      v.x *= -1
      v.y *= -1
      v.order = "ZXY"

      this.camera.rotation.set(v.x, v.y, v.z)
    }
  }

  enableMainCamera() {
    this.camera.position.set(
      this.startPosition.x,
      this.startPosition.y,
      this.startPosition.z,
    )
    this.camera.rotation.set(
      this.startRotation.x,
      this.startRotation.y,
      this.startRotation.z,
    )
    this.controller.enabled = true
  }

  cacheOfficeObjects() {
    this.box = this.scene.children[0].children.filter((e) =>
      e.name.includes("boundingbox"),
    )

    this.krov = this.scene.children[0].children.filter(
      (e) => e.name.includes("krov-") || e.name.includes("krvo-"),
    )

    this.target_1 = this.scene.getObjectByName("camera_target")
    this.target_2 = this.scene.getObjectByName("camera_target2")

    this.cameraPositions = this.scene.children[0].children.filter((e) =>
      e.name.includes("camera_0"),
    )

    this.layout_1 = this.scene.getObjectByName("layout_v1")
    this.logo = this.scene.getObjectByName("gtc-x-logo_02")
    this.layout_2 = this.scene.getObjectByName("layout_v2")

    if (this.layout_1) {
      this.layout_1.visible = false
    }

    if (this.layout_2) {
      this.layout_2.visible = false
    }

    this.floor_VII = this.scene.getObjectByName("VII-sprat")
    this.floor_VIII = this.scene.getObjectByName("VIII-sprat")

    if (this.texture_roof && this.texture_fence) {
      this.switchLightmaps(this.texture_roof, this.krov[0])
      this.switchLightmaps(this.texture_fence, this.krov[1])
    }

    this.lights = this.scene.children[0].children.filter(
      (e) =>
        e.name.includes("ceiling-lights_7") ||
        e.name.includes("ceiling-lights_8"),
    )
  }

  tweenTo(
    object: THREE.Object3D,
    to: Vector3,
    delay = 0,
    duration = 1000,
  ): TWEEN.Tween<THREE.Vector3> {
    const nestPos = to.add(object.position)
    const tween = new TWEEN.Tween(object.position) // Create a new tween that modifies 'coords'.
      .to(nestPos, duration)
      .onUpdate((t, e) => {
        this.fade(1 - e, object)
      })
      .onComplete(() => {
        object.visible = false
      })
      .easing(TWEEN.Easing.Quadratic.InOut)
      .delay(delay)
      .start()
    return tween
  }

  toggleTransparency(object: Object3D) {
    const materials: THREE.Material | Material[] = (object as Mesh).material

    for (let x = 0; x < (materials as THREE.Material[]).length; x++) {
      const element = (materials as THREE.Material[])[x]
      // if(element.type == "Mesh")
      element.opacity = 1
      element.transparent = false
      element.needsUpdate = true
    }
  }

  changeCameraView(camera: THREE.PerspectiveCamera, toPosition: Vector3) {
    if (this.box === undefined) return
    const bBox = this.box[0] as Mesh
    bBox.geometry?.computeBoundingSphere()

    const boundingBox = new THREE.Box3()

    boundingBox.setFromObject(bBox)

    if (bBox.geometry.boundingSphere) {
      const size = new Vector3()
      boundingBox.getSize(size)

      const maxDim = Math.max(size.x, size.y, size.z)
      const fov = camera.fov * (Math.PI / 180)
      let cameraZ = Math.abs((maxDim / 2) * Math.tan(fov * 2))
      if (this.camera.aspect < 1.0) {
        cameraZ *= 2.4
      } else {
        cameraZ *= 1.3
      }

      const to = new Vector3()
      to.copy(this.cameraTarget)

      const direction = toPosition.sub(to).normalize()
      const nextPosition = to.add(direction.multiplyScalar(cameraZ as number))
      return nextPosition
      // camera.position.set(camera.position.x, nextPosition.y, camera.position.z)
    }
  }

  getNestFitCameraPosition(camera: PerspectiveCamera) {
    const box = this.scene.children[0].children.filter((e) =>
      e.name.includes("boundingbox"),
    )

    const bBox = box[0] as Mesh

    if (bBox == undefined) return 0
    if (bBox.geometry.boundingSphere == undefined) {
      bBox.geometry.computeBoundingSphere()
    }
    const boundingSphere = bBox.geometry.boundingSphere

    if (boundingSphere) {
      const cameraDistance = new Vector3()
      camera.getWorldPosition(cameraDistance)

      const target = this.cameraTarget.equals(new Vector3())
        ? boundingSphere.center
        : this.cameraTarget

      const dist = cameraDistance.distanceTo(target)
      const height = boundingSphere?.radius * 1.2 // desired height to fit
      let nextFov = 0
      if (camera.aspect < 1) {
        nextFov =
          2 * Math.atan(height / camera.aspect / (2 * dist)) * (180 / Math.PI)
      } else {
        nextFov = 2 * Math.atan(height / (2 * dist)) * (180 / Math.PI)
      }

      return nextFov
    }
  }

  tweenFrom(
    object: THREE.Object3D,
    from: Vector3,
    delay = 0,
    duration = 1000,
  ): TWEEN.Tween<THREE.Vector3> {
    const nestPos = from.add(object.position)
    object.visible = true
    const tween = new TWEEN.Tween(object.position) // Create a new tween that modifies 'coords'.
      .to(nestPos, duration)
      .onUpdate((t, e) => {
        this.fade(e, object)
      })
      .onComplete(() => {
        this.toggleTransparency(object)
      })
      .easing(TWEEN.Easing.Quadratic.InOut)
      .delay(delay)
      .start()
    return tween
  }

  lerp(a: number, b: number, f: number) {
    return a + f * (b - a)
  }
  getZoomScale() {
    return Math.pow(0.95, 0.5)
  }
  zoomIn(zoomIn: boolean) {
    this.zoomedIn = zoomIn
    if (zoomIn) {
      const zoom = new Vector3(0, 0, 0)
      const toZoom = new Vector3(0.5, 0.5, 0.5)
      new TWEEN.Tween(zoom)
        .to(toZoom, 300)
        .onUpdate((t, e) => {
          this.controller.zoomIn(this.getZoomScale())
        })
        .easing(TWEEN.Easing.Cubic.InOut)
        .delay(0)
        .start()
    } else {
      const zoom = new Vector3(0, 0, 0)
      const toZoom = new Vector3(0.5, 0.5, 0.5)
      new TWEEN.Tween(zoom)
        .to(toZoom, 300)
        .onUpdate((t, e) => {
          this.controller.zoomOut(this.getZoomScale())
        })
        .easing(TWEEN.Easing.Cubic.InOut)
        .delay(0)
        .start()
    }
  }

  onRotateY(deg: number) {
    this.controller.rotate(0, (deg * Math.PI) / 180)
  }

  tweenCameraTo(
    camera: THREE.PerspectiveCamera,
    nextPosition: Vector3,
    nextRotation: Vector3 | Euler,
    target: Object3D | any,
    delay = 250,
    duration = 1000,
  ) {
    this.finishedTransition = false
    const camPos = new Vector3()

    const targetPos = new Vector3(
      this.controller.target.x,
      this.controller.target.y,
      this.controller.target.z,
    )

    const nextTargetPos = new Vector3(
      target.position.x,
      target.position.y,
      target.position.z,
    )

    new TWEEN.Tween(targetPos)
      .to(nextTargetPos, duration)
      .onUpdate((t, e) => {
        this.controller.target.set(t.x, t.y, t.z)
        this.controller.update()
      })
      .easing(TWEEN.Easing.Quadratic.InOut)
      .start()

    this.controller.target
    this.controller.enableRotate = false
    camera.getWorldPosition(camPos)
    const next = this.changeCameraView(camera, nextPosition) as Vector3

    const tween = new TWEEN.Tween(camera.position)
      .to(next, duration)
      .onComplete(() => {
        console.log("comlete")
      })
      .easing(TWEEN.Easing.Quadratic.InOut)
      .delay(delay)
      .start()

    const Azim = this.controller.getAzimuthalAngle()
    const Polar = this.controller.getPolarAngle()

    const newAzim = nextRotation.z
    const newPolar = nextRotation.x
    const rot = new Vector2(Azim, Polar)
    const rotNext = new Vector2(newAzim, newPolar)
    const tweenRot = new TWEEN.Tween(rot)
      .to(rotNext, duration)
      .onUpdate((e, t) => {
        this.controller.setPolarAngle(e.y)
        this.controller.setAzimuthalAngle(e.x)
      })
      .onComplete(() => {
        this.controller.saveState()
        this.controller.enableRotate = true
        this.finishedTransition = true
      })
      .easing(TWEEN.Easing.Cubic.InOut)
      .delay(delay)
      .start()
  }

  fade(to: number, object: THREE.Object3D) {
    const materials: THREE.Material | Material[] = (object as Mesh).material

    for (let x = 0; x < (materials as THREE.Material[]).length; x++) {
      const element = (materials as THREE.Material[])[x]
      element.opacity = to
      element.transparent = true
      // if (to < 1) {
      // } else {
      //   element.transparent = false
      // }
      element.needsUpdate = true
    }
  }

  loadLightmap(path: string): Promise<THREE.Texture> {
    return new Promise((resolve) => {
      const tgaLoader = new TGALoader()
      tgaLoader.load(
        path,
        function (textureData: THREE.Texture) {
          const tgaTexture = textureData
          tgaTexture.flipY = true
          tgaTexture.encoding = RGBM7Encoding
          tgaTexture.needsUpdate = true

          resolve(tgaTexture)
        },
        undefined,
        (e) => {
          console.log(e.error)
        },
      )
    })
  }

  startLightmap = null

  floorMat: any = undefined
  envMat: any = undefined

  switchLightmaps(to: THREE.Texture, object: THREE.Object3D) {
    const materials: THREE.Material | Material[] = (object as Mesh)?.material

    if (materials === undefined) return

    for (let x = 0; x < (materials as THREE.Material[]).length; x++) {
      const element: MeshLambertMaterial = (materials as THREE.Material[])[
        x
      ] as MeshLambertMaterial
      element.lightMap = to
    }
  }

  startPosition: Vector3 = new Vector3(0, 0, 0)
  startRotation: Euler = new Euler(0, 0, 0)
  startAzim = 0
  startPolar = 0

  hideRoof(layout = 0) {
    if (this.tweenedOut || this.finishedTransition === false) return
    this.finishedTransition = false
    this.camera.getWorldPosition(this.startPosition)
    this.camera.rotation.copy(this.startRotation)
    this.startAzim = this.controller.getAzimuthalAngle()
    this.startPolar = this.controller.getPolarAngle()
    this.zoomedIn = false
    let targetPos = new Vector3()
    if (this.target_2) {
      targetPos = new Vector3(
        this.target_2.position.x,
        this.target_2.position.y,
        -this.target_2.position.z,
      )
    }

    this.tweenCameraTo(
      this.camera,
      // this.camera.aspect < 1.0
      // ? new THREE.Vector3(-0.19, 0.3, -0.14)
      // : new THREE.Vector3(-0.15, 0.3, -0.14),
      targetPos.add(new THREE.Vector3(0, 0.2, 0)),
      this.camera.aspect < 1.0
        ? new Vector3(0, 0, Math.PI / 2)
        : new Vector3(0, 0, 0),
      this.target_2,
      900,
    )

    if (this.floor_VII) {
      this.floor_VII.visible = false
    }

    if (layout === 0) {
      if (this.layout_1) {
        this.layout_1.visible = true
      }
    } else {
      if (this.layout_2) {
        this.layout_2.visible = true
      }
    }

    this.tweenedOut = true

    this.krov?.forEach((e) => {
      this.tweenTo(e, new THREE.Vector3(0, 0.1, 0))
    })

    if (this.logo) {
      this.tweenTo(this.logo, new THREE.Vector3(0, 0.1, 0))
    }

    if (this.floor_VIII) {
      this.tweenTo(this.floor_VIII, new THREE.Vector3(0, 0.1, 0), 100)
    }

    if (this.lights) {
      this.lights.forEach((e) => {
        this.envMat = ((e as Mesh).material as MeshLambertMaterial).lightMap
        if (e.name == "ceiling-lights_7")
          this.tweenTo(e, new THREE.Vector3(0, 0.1, 0), 100)
        else this.tweenTo(e, new THREE.Vector3(0, 0.1, 0))
      })
    }
  }

  showRoof() {
    if (!this.tweenedOut || this.finishedTransition === false) return
    this.finishedTransition = false
    this.zoomedIn = false
    this.tweenCameraTo(
      this.camera,
      this.startPosition,
      new Euler(this.startPolar, 0, this.startAzim), //this.startRotation,
      this.target_1,
      0,
    )
    this.tweenedOut = false

    if (this.floor_VII) {
      this.floor_VII.visible = true
    }
    if (this.layout_1) {
      this.layout_1.visible = false
    }
    if (this.layout_2) {
      this.layout_2.visible = false
    }

    if (this.logo) {
      this.tweenFrom(this.logo, new THREE.Vector3(0, -0.1, 0), 100)
    }

    this.krov?.forEach((e) => {
      this.tweenFrom(e, new THREE.Vector3(0, -0.1, 0), 100)
    })
    if (this.floor_VIII) {
      this.tweenFrom(this.floor_VIII, new THREE.Vector3(0, -0.1, 0))
    }
    if (this.lights) {
      this.lights.forEach((e) => {
        this.envMat = ((e as Mesh).material as MeshLambertMaterial).lightMap
        if (e.name == "ceiling-lights_7")
          this.tweenFrom(e, new THREE.Vector3(0, -0.1, 0))
        else this.tweenFrom(e, new THREE.Vector3(0, -0.1, 0), 100)
      })
    }
  }
}
